import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { getPreSignedUrl, uploadFilesTos3 } from "src/services/upload";
import { Spinner } from "reactstrap";
import { getFileUploadName, getFileType, extractValuesForSimMCS } from "src/utils/common";
import { alertState, dataJson, uploadingFile, uploadingJsonFile } from "src/recoil/atoms/global";
import { BUTTON_UPLOAD_POSITION, FILE_TYPE, UPLOAD_TYPE } from "src/constant/common";
import { useSetRecoilState } from "recoil";
import FileNameDisplay from "./FileNameDisplay";
import ProtectedComponent from "src/components/Permission";

const FormUpload = ({
  register,
  validate,
  errors,
  name,
  isMultiple = true,
  placeholder,
  classInput,
  classGroup,
  handleUpdateAttachments,
  files = [],
  uploadType,
  fileTypeAllow = [FILE_TYPE.JSON],
  fileTypeValidateMsg = "validate_attachments",
  useNewApiUpload,
  onUpload,
  errorMessage,
  isMappingFields = false,
  isUploading = false,
  isReplaceFileWhenUpload = false,
  isShowButtonUpload = true,
  isShowFileIcon = true,
  isShowButtonDelete = true,
  buttonUploadPosition = BUTTON_UPLOAD_POSITION.BOTTOM,
  isCheckDuplicateFileName = false,
  updateAt,
}) => {
  const { t } = useTranslation("project-instance", "common");
  const [toggle, setToggle] = useState(false);
  const [itemDelete, setItemDelete] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingOtherFile, setUploadingOtherFile] = useState(false);
  const showAlert = useSetRecoilState(alertState);
  const setDataJson = useSetRecoilState(dataJson);
  const setIsUploadingJsonFile = useSetRecoilState(uploadingJsonFile);
  const setIsUploadingFile = useSetRecoilState(uploadingFile);
  const [fileUpdateAt, setFileUpdateAt] = useState(updateAt);
  const acceptFiles = () => fileTypeAllow.map((item) => `.${item}`).join();

  const readJsonFile = (file) => {
    if (file && getFileType(file.name) === FILE_TYPE.JSON) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const jsonData = JSON.parse(e.target.result);

          extractValuesForSimMCS("sigma_I_s", jsonData.MCS, ["x", "y", "z"], jsonData);
          extractValuesForSimMCS("sigma_I_e", jsonData.MCS, ["x", "y", "z"], jsonData);

          setDataJson(jsonData);
          setIsUploadingJsonFile(true);
        } catch (error) {
          console.error(error);
        }
      };
      reader.readAsText(file);
    }
  };

  const handleUploadFile = async (e) => {
    if (e?.target?.files?.length > 10) {
      showAlert({
        status: true,
        message: t("limit_attachments"),
        type: "danger",
      });
      return;
    }

    isMappingFields && readJsonFile(e?.target?.files[0]);

    const fileData = [];

    [...e?.target?.files].map((item) => {
      const fileType = getFileType(item.name);
      if (fileTypeAllow?.length && fileTypeAllow.indexOf(fileType) === -1) {
        return showAlert({
          status: true,
          message: t(fileTypeValidateMsg, {
            file_name: item.name,
            file_type: fileTypeAllow.join(", "),
          }),
          type: "danger",
        });
      }
      if (isCheckDuplicateFileName && files.some((file) => getFileUploadName(file) === item.name)) {
        return showAlert({
          status: true,
          message: t("validate_duplicate_file_name"),
          type: "danger",
        });
      }
      return fileData.push(item);
    });

    if (fileData.length > 0) {
      setUploading(true);
      setIsUploadingFile(true);
      onUpload(true);

      Promise.all([...fileData].map(uploadFile))
        .then((res) => {
          res = res.filter((item) => item !== undefined);
          if (isReplaceFileWhenUpload) {
            handleUpdateAttachments([...res]);
          } else {
            handleUpdateAttachments([...files, ...res]);
          }
        })
        .catch((e) => {
          setDataJson(null);
          showAlert({
            status: true,
            message: e?.response?.data?.error?.message || e?.response?.data?.message || t("common:network_error"),
            type: "danger",
          });
        })
        .finally(() => {
          onUpload(false);
          setUploading(false);
          setIsUploadingFile(false);
          setFileUpdateAt(null);
          if (isMappingFields) {
            const fileType = getFileType(fileData[0].name);
            fileType === FILE_TYPE.JSON && setIsUploadingJsonFile(false);
          }
        });
    }
  };

  const uploadFile = async (file) => {
    if (!file.size) {
      showAlert({
        status: true,
        message: t("file_error"),
        type: "danger",
      });
      return;
    }
    let presignedUrl = {};
    const data = {
      file_name: file?.name,
      file_type: uploadType || UPLOAD_TYPE.RAW,
    };

    presignedUrl = await getPreSignedUrl({ data });

    const { url, fields } = presignedUrl;
    return await uploadFilesTos3({
      url,
      fields,
      file,
    });
  };

  const handleDeleteAttachments = (index) => {
    const newFiles = files;

    if (isMappingFields) {
      const fileName = getFileUploadName(newFiles[0]);
      if (getFileType(fileName) === FILE_TYPE.JSON) {
        setDataJson(null);
      }
    }

    newFiles.splice(index, 1);

    handleUpdateAttachments([...newFiles]);
    setItemDelete(null);
    setToggle(false);
  };

  useEffect(() => {
    setUploadingOtherFile(isUploading);
  }, [isUploading]);

  useEffect(() => {
    setFileUpdateAt(updateAt);
  }, [updateAt]);

  return (
    <div className={classNames("form-group position-relative", classGroup)} name={name}>
      <div className="d-flex align-items-center">
        {files?.map((item, index) => {
          return (
            <div className="file-upload-container" key={index}>
              {item && (
                <div
                  className={classNames("card shadow-none border width-by-percent-100 m-b-7", {
                    "width-by-px-256": isShowButtonUpload && buttonUploadPosition === BUTTON_UPLOAD_POSITION.END,
                  })}
                >
                  <div className="p-2 d-flex justify-content-between align-items-center gap-1">
                    <div className="row align-items-center">
                      {isShowFileIcon && (
                        <div className="col-auto">
                          <div className="avatar-sm">
                            <span className="avatar-title bg-soft-warning text-warning rounded fs-24">
                              <i className="bi bi-file-earmark-zip"></i>
                            </span>
                          </div>
                        </div>
                      )}
                      <div className="col ps-1">
                        <FileNameDisplay
                          item={item}
                          name={name}
                          buttonUploadPosition={buttonUploadPosition}
                          isShowButtonUpload={isShowButtonUpload}
                          updateAt={fileUpdateAt}
                        />
                      </div>
                    </div>
                    {isShowButtonDelete && (
                      <div>
                        <i
                          className="bi bi-trash text-danger f-s-24 user-select-pointer"
                          role="button"
                          onClick={() => {
                            setToggle(true);
                            setItemDelete(index);
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {isShowButtonUpload && buttonUploadPosition === BUTTON_UPLOAD_POSITION.END && (
          <ProtectedComponent id={`project_instance_detail_upload_${name}`}>
            <div className="d-flex align-items-center">
              <label
                htmlFor={name}
                className={`btn btn-secondary min-width-by-px-160 width-by-px-160 text-nowrap ${
                  (uploading || (!isMultiple && files.length) || uploadingOtherFile) && "disabled"
                }`}
              >
                {uploading && isReplaceFileWhenUpload ? <Spinner size="sm" /> : t("select_file")}
              </label>
              <input
                {...(register &&
                  register(name, {
                    ...(validate && { validate: validate }),
                  }))}
                draggable
                multiple={isMultiple}
                type="file"
                name={name}
                id={name}
                hidden
                placeholder={placeholder}
                className={classNames("form-control", classInput)}
                onChange={handleUploadFile}
                accept={acceptFiles()}
                onClick={(event) => (event.currentTarget.value = null)}
                disabled={uploading || (!isMultiple && files.length) || uploadingOtherFile}
              />
            </div>
          </ProtectedComponent>
        )}
      </div>

      {uploading && !isReplaceFileWhenUpload && (
        <div className="p-2 d-flex align-items-center justify-content-center file-upload-container">
          <Spinner color="secondary" />
        </div>
      )}
      {isShowButtonUpload && buttonUploadPosition === BUTTON_UPLOAD_POSITION.BOTTOM && (
        <>
          <label
            htmlFor={name}
            className={`btn btn-secondary width-by-px-160 text-nowrap ${
              (uploading || (!isMultiple && files.length) || uploadingOtherFile) && "disabled"
            }`}
          >
            {t("select_file")}
          </label>
          <input
            {...(register &&
              register(name, {
                ...(validate && { validate: validate }),
              }))}
            draggable
            multiple={isMultiple}
            type="file"
            name={name}
            id={name}
            hidden
            placeholder={placeholder}
            className={classNames("form-control", classInput)}
            onChange={handleUploadFile}
            accept={acceptFiles()}
            onClick={(event) => (event.currentTarget.value = null)}
            disabled={uploading || (!isMultiple && files.length) || uploadingOtherFile}
          />
        </>
      )}
      {errors?.[name] && <div className={`text-danger font-12 position-absolute`}>{errors?.[name]?.message}</div>}
      {errorMessage && <div className={`text-danger font-12 position-absolute`}>{errorMessage}</div>}

      <Modal isOpen={toggle} toggle={() => setToggle(false)} backdrop="static">
        <ModalHeader>{t("delete_modal.title_attachments")}</ModalHeader>
        <ModalBody>
          <div>{t("delete_modal.sure")}</div>
          <div>{t("delete_modal.recover")}</div>
        </ModalBody>
        <ModalFooter>
          <span className="btn btn-secondary btn-sm" role="button" onClick={() => setToggle(false)}>
            {t("delete_modal.cancel")}
          </span>
          <span className="btn btn-danger btn-sm" role="button" onClick={() => handleDeleteAttachments(itemDelete)}>
            {t("delete_modal.delete")}
          </span>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default FormUpload;
